'use client';

import React, { useEffect, useState, useRef } from 'react';
import VariantDetails from './VariantDetails';

function AnimatedNumber({ value, startAnimation }) {
  const valStr = String(value || "");
  const match = valStr.trim().match(/^([\d.]+)(.*)$/);

  const target = match && !isNaN(parseFloat(match[1])) ? parseFloat(match[1]) : null;
  const suffix = match ? match[2] : "";
  const hasDecimal = match ? match[1].includes('.') : false;

  // Set initial value so we don't have layout jumps before the animation starts
  const [displayValue, setDisplayValue] = useState(
    target !== null ? (hasDecimal ? "0.0" : "0") : valStr
  );

  useEffect(() => {
    if (target === null) {
      setDisplayValue(valStr);
      return;
    }

    // If animation hasn't started, hold at zero
    if (!startAnimation) {
      setDisplayValue(hasDecimal ? "0.0" : "0");
      return;
    }

    let startTime;
    const duration = 2000; // 2 seconds animation
    let animationFrameId;

    const updateCounter = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsedTime = currentTime - startTime;

      if (elapsedTime < duration) {
        const progress = elapsedTime / duration;
        // Easing function for smooth deceleration
        const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        const currentVal = target * easeOut;

        const formattedNum = hasDecimal
          ? currentVal.toFixed(1)
          : Math.floor(currentVal);

        setDisplayValue(formattedNum);
        animationFrameId = requestAnimationFrame(updateCounter);
      } else {
        setDisplayValue(match && target !== null ? match[1] : valStr); // Guarantee exact final number string
      }
    };

    animationFrameId = requestAnimationFrame(updateCounter);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, startAnimation, target, suffix, hasDecimal, valStr]);

  return (
    <div className="flex items-baseline gap-1" dir="ltr">
      <span className="text-[48px] md:text-[56px] lg:text-[48px] xl:text-[72px] font-light text-gray-900 latinNumbers tracking-tighter leading-none">
        {displayValue}
      </span>
      {suffix && (
        <span className="text-xl md:text-lg lg:text-2xl xl:text-3xl font-light text-gray-800 latinNumbers">
          {suffix}
        </span>
      )}
    </div>
  );
}

export default function VariantKeySpecs({ car }) {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    // Crucial: Reset visibility to false when the car changes so the animation can re-trigger!
    setIsVisible(false);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
        // We do not disconnect, so if the user scrolls away and back, we could re-trigger if we wanted.
        // But typically, we just want it to trigger once, or whenever it comes into view.
        // Let's disconnect so it only plays once per mount/car load.
        observer.disconnect();
      }
    }, { threshold: 0.3 }); // Trigger when 30% of the container is visible

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [car]); // Re-attach observer and reset visibility if the car changes

  if (!car) return null;

  // Attempt to extract 3 key specifications to show prominently
  const specs = [
    {
      label: "شتاب 0-100 (ثانیه)",
      value: car.technical_info?.acceleration_0_100_s || "-"
    },
    {
      label: "قدرت موتور",
      value: car.technical_info?.gasoline_engine_power_hp
        ? `${car.technical_info.gasoline_engine_power_hp} hp`
        : (car.technical_info?.electric_motor_power_kw ? `${car.technical_info.electric_motor_power_kw} kw` : "-")
    },
    {
      label: "مصرف سوخت ترکیبی",
      value: car.technical_info?.fuel_consumption_l_per_100km
        ? `${String(car.technical_info.fuel_consumption_l_per_100km).replace(/[^\d.]/g, '')} L/100km`
        : "-"
    },
  ];

  return (
    <div ref={containerRef}>
      <div className="w-full flex flex-col md:flex-row items-center justify-between gap-6 md:gap-10 mt-16 px-4 md:px-8 lg:px-20">

        {/* Right Side (Image) - 40% on tablet, 55% on desktop */}
        <div className="w-full md:w-[40%] lg:w-[55%] flex justify-center">
          {/* We prioritize main_image to ensure we don't get broken images. */}
          <img
            src={car.detail_image_2 || car.main_image || car.slider_image}
            alt={car.english_name}
            className="w-full h-auto object-contain drop-shadow-2xl"
          />
        </div>

        {/* Left Side (Specs) - 45% on tablet, 25% on desktop. In RTL, first element is on the left. */}
        <div className="w-full md:w-[45%] lg:w-[25%] flex flex-col gap-10 lg:gap-16 text-center md:text-left ml-0 lg:ml-10 items-center md:items-start text-right">
          {specs.map((spec, idx) => (
            <div key={`${car?.id}-${idx}`} className="flex flex-col items-center md:items-start">
              <AnimatedNumber value={spec.value} startAnimation={isVisible} />
              <span className="text-gray-400 text-xs lg:text-sm font-light mt-1">
                {spec.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Details Button - Outside main container, perfectly aligned with specs column */}
      <div className="w-full flex justify-between px-4 md:px-8 lg:px-20 mt-8 md:mt-12">
        {/* Spacer to push button exactly to the specs column */}
        <div className="hidden md:block md:w-[40%] lg:w-[55%]"></div>
        <div className="w-full md:w-[45%] lg:w-[25%] flex justify-center md:justify-start ml-0 lg:ml-10 items-center md:items-start">
          <VariantDetails
            car={car}
            buttonClassName="w-full md:w-auto justify-center px-10 py-3 bg-gray-300 hover:bg-gray-100 text-[#2b2b2b] font-bold rounded-button-mob md:rounded-button-desk shadow-lg transition-transform transform hover:scale-105 flex items-center gap-2 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
}
